import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import {
  Box,
  Container,
  IconButton,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { Grid } from "@mui/system";
import { useBackofficeConstant } from "components/context/ConstantProvider";
import useQueryStringState from "components/hooks/useQueryStringState";
import { LabelValueForText } from "components/ui/atoms/LabelValue";
import { InfoButton } from "components/ui/customs/components/ModelInfoButton";
import useCustomDataSource from "components/ui/customs/useCustomDataSource";
import i18n from "i18n";
import { useEffect } from "react";
import DealSlide1 from "./slides/DealSlide1";
import DealSlide2 from "./slides/DealSlide2";

export const DealVisualizationInformationForm = ({
  deal,
  state,
  setState,
  dealVisualizationInformation,
}) => {
  const valuationPeriods = useBackofficeConstant("operations.ValuationPeriod");
  const all_funds = useBackofficeConstant("backoffice.Fund");
  const funds = all_funds.filter((f) =>
    deal.deal_funds.find((df) => df.fund === f.id),
  );

  return (
    <>
      <Grid container spacing={2} paddingY={2}>
        <TextField
          select
          size="small"
          value={state.fundId}
          onChange={(e) => setState({ ...state, fundId: e.target.value })}
        >
          {funds.map((f) => (
            <MenuItem key={f.id} value={f.id}>
              {f._title}
            </MenuItem>
          ))}
          {!funds.length && (
            <MenuItem value="">
              <em>No Fund Available</em>
            </MenuItem>
          )}
        </TextField>
        <TextField
          select
          size="small"
          value={state.valuationPeriodId}
          onChange={(e) =>
            setState({ ...state, valuationPeriodId: e.target.value })
          }
        >
          {valuationPeriods.map((vp) => (
            <MenuItem key={vp.id} value={vp.id}>
              {vp._title}
            </MenuItem>
          ))}
          {!valuationPeriods.length && (
            <MenuItem value="">
              <em>No Valuation Periods Available</em>
            </MenuItem>
          )}
        </TextField>
        <TextField
          select
          size="small"
          value={state.language}
          onChange={(e) => setState({ ...state, language: e.target.value })}
        >
          <MenuItem key={"en"} value={"en"}>
            English
          </MenuItem>
          <MenuItem key={"fr"} value={"fr"}>
            Français
          </MenuItem>
        </TextField>
      </Grid>
      <Stack spacing={2} alignItems="center">
        <Box width="100%">
          <LabelValueForText
            label="Stored Portfolio"
            value={dealVisualizationInformation?.stored_portfolio?._title}
            isMissing={!dealVisualizationInformation?.stored_portfolio}
          />
        </Box>
      </Stack>
    </>
  );
};

export const useConfigurationLinks = (dealVisualizationInformation) => {
  if (!dealVisualizationInformation) {
    return null;
  }

  const configurationLinks = [
    {
      key: "deal",
      label: "Deal",
      modelName: "backoffice.Deal",
      id: dealVisualizationInformation.deal?.id,
    },
    {
      key: "deal_information",
      label:
        dealVisualizationInformation.deal_information?._title ||
        "Deal Information",
      modelName: "reporting.DealInformation",
      id: dealVisualizationInformation.deal_information?.id,
    },
    {
      key: "deal_reporting",
      label:
        dealVisualizationInformation.deal_reporting?._title || "Deal Reporting",
      modelName: "reporting.DealReporting",
      id: dealVisualizationInformation.deal_reporting?.id,
    },
    {
      key: "deal_exit",
      label: dealVisualizationInformation.deal_exit?._title || "Deal Exit",
      modelName: "reporting.DealExit",
      id: dealVisualizationInformation.deal_exit?.id,
    },
    {
      key: "deal_fund",
      label: "Deal Fund Link",
      modelName: "backoffice.DealFund",
      id: dealVisualizationInformation.deal_fund?.id,
    },
    {
      key: "captable",
      label: dealVisualizationInformation.captable?._title || "Captable",
      modelName: "reporting.CaptableLight",
      id: dealVisualizationInformation.captable?.id,
    },
  ];
  return configurationLinks;
};

const DealHome = ({ page, data: deal }) => {
  const valuationPeriods = useBackofficeConstant("operations.ValuationPeriod");
  const [qsState, setQState] = useQueryStringState({
    fundId: deal.deal_funds?.[0]?.fund,
    valuationPeriodId: valuationPeriods[0].id,
    scaleActive: true,
    language: "en",
  });
  const handleToggleScaleActive = () => {
    setQState({ ...qsState, scaleActive: !qsState.scaleActive });
  };
  const { useGetByIds } = useCustomDataSource(
    "visualization.DealVisualizationInformation",
  );
  const {
    data: dealVisualizationInformation,
    isSuccess,
    status,
  } = useGetByIds({
    dealId: deal.id,
    fundId: qsState.fundId,
    valuationPeriodId: qsState.valuationPeriodId,
  });

  useEffect(() => {
    i18n.changeLanguage(qsState.language);
  }, [qsState.language]);

  return (
    <Container maxWidth="md">
      <DealVisualizationInformationForm
        deal={deal}
        state={qsState}
        setState={setQState}
        dealVisualizationInformation={dealVisualizationInformation}
      />
      <Box display={"flex"}>
        <IconButton
          onClick={handleToggleScaleActive}
          sx={{
            marginLeft: "auto",
            right: "-8px",
          }}
          size="small"
        >
          {qsState.scaleActive ? (
            <FullscreenIcon fontSize="small" />
          ) : (
            <FullscreenExitIcon fontSize="small" />
          )}
        </IconButton>
      </Box>
      <DealSlide1
        dealVisualizationInformation={dealVisualizationInformation}
        scaleActive={qsState.scaleActive}
      />
      <DealSlide2
        dealVisualizationInformation={dealVisualizationInformation}
        scaleActive={qsState.scaleActive}
      />
      <InfoButton
        label="Deal Visualization Information"
        data={dealVisualizationInformation}
      />
    </Container>
  );
};

export default DealHome;
